import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tree } from 'react-arborist';
import type { NodeRendererProps } from 'react-arborist';
import {
  Plus, Pencil, Search, Filter, X, Download, Ban, AlertTriangle, ChevronDown, ChevronRight, List, GitBranch,
} from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { NodeTypeIconChip } from '@/components/NodeTypeIcon';
import { downloadAsCsv } from '@/lib/csv-export';
import { useScopeStore, type NodeTreeItem, type NodesResponse } from '@/stores/scope';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';

/** `/masters/stores` (旧 StoresPage 置換、v0.2.5 S-11、T-1105)
 *  設計: docs/design/v025-config-ui.md §2-4。「ツリー」「葉店舗のみ」の2モード切替。
 *  葉店舗のみモードは既存 StoresPage の全機能をそのまま維持する (店舗CRUD・停止確認ダイアログ等)。
 *  単店企業 (hierarchy_management OFF) では自動的に葉店舗のみモードにフォールバックする (§0.5 E-3)。 */

type Store = {
  store_id: string;
  store_code: string;
  store_name: string;
  address: string | null;
  effective_from: string;
  effective_to: string | null;
  status: 'active' | 'closed';
};

type StoreForm = {
  store_id?: string;
  store_code: string;
  store_name: string;
  address?: string;
  effective_from: string;
  effective_to?: string;
  status?: 'active' | 'closed';
};

type StoreDeps = {
  store_id: string;
  store_name: string;
  status: 'active' | 'closed';
  dependencies: {
    sales_details: number;
    delivery_details: number;
    delivery_sales_details: number;
    labor_cost_details: number;
    opex_details: number;
    menu_master: number;
    ingredient_master: number;
    delivery_service_master: number;
    kpi_threshold_master: number;
    user_stores: number;
  };
  total: number;
};

const DEP_LABELS: Record<keyof StoreDeps['dependencies'], string> = {
  sales_details: '売上明細',
  delivery_details: '納品明細',
  delivery_sales_details: 'デリバリー売上',
  labor_cost_details: '人件費',
  opex_details: '諸経費',
  menu_master: 'メニューマスタ',
  ingredient_master: '食材マスタ',
  delivery_service_master: 'デリバリー業者マスタ',
  kpi_threshold_master: 'KPI閾値マスタ',
  user_stores: 'ユーザ権限割当',
};

export function NodesPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const hierarchyManagementEnabled = useScopeStore((s) => s.hierarchyManagementEnabled);
  // v0.3 (S-11 DL権限管理): export:view権限が無いユーザーにはCSV出力ボタンを非表示にする
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canExport = hasPermissionForFeatureAction('export', 'view');
  const [mode, setMode] = useState<'tree' | 'flat'>('tree');
  const effectiveMode = hierarchyManagementEnabled ? mode : 'flat';
  // Design MAJOR対応 (2026-08-03レビュー): ツリーモードは既定で include_closed を送っておらず
  // 停止中ノードが最初から取得できなかった。フラットモードの「停止中のみ」フィルタと同様に、
  // ツリーモードでも停止中ノードを表示できるようにする (HierarchyPageの「停止中も表示」と同じパターン)
  const [showClosedTree, setShowClosedTree] = useState(false);

  const { data: stores = [], isLoading } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes', 'flat-tree', showClosedTree],
    queryFn: () => apiFetch<NodesResponse>(`/nodes${showClosedTree ? '?include_closed=1' : ''}`),
    enabled: effectiveMode === 'tree',
  });

  const [editing, setEditing] = useState<StoreForm | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'closed'>('all');
  const [stopping, setStopping] = useState<Store | null>(null);

  const { data: stoppingDeps, isLoading: stoppingDepsLoading } = useQuery<StoreDeps>({
    queryKey: ['store-dependencies', stopping?.store_id],
    queryFn: () => apiFetch<StoreDeps>(`/stores/${stopping!.store_id}/dependencies`),
    enabled: Boolean(stopping),
  });

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return stores.filter((s) => {
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (q && !(s.store_code.toLowerCase().includes(q) || s.store_name.toLowerCase().includes(q) || (s.address ?? '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [stores, searchText, filterStatus]);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort<Store>(filtered as unknown as Record<string, unknown>[] & Store[], {
    defaultKey: 'store_code',
    defaultDir: 'asc',
    accessors: {
      store_code: (s: Store) => Number(s.store_code) || s.store_code,
      store_name: (s: Store) => s.store_name,
      address: (s: Store) => s.address ?? '',
      effective_from: (s: Store) => s.effective_from,
      status: (s: Store) => s.status,
    } as unknown as Record<string, (s: Store) => unknown>,
  });

  const hasAnyFilter = Boolean(searchText || filterStatus !== 'all');
  const clearFilters = () => { setSearchText(''); setFilterStatus('all'); };

  const openEditFromStoreId = (storeId: string) => {
    const s = stores.find((x) => x.store_id === storeId);
    if (!s) return;
    setEditing({
      store_id: s.store_id, store_code: s.store_code, store_name: s.store_name,
      address: s.address ?? '', effective_from: s.effective_from, effective_to: s.effective_to ?? '', status: s.status,
    });
  };

  const createMut = useMutation({
    mutationFn: (form: StoreForm) => apiFetch('/stores', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['nodes'] });
      setEditing(null);
      toast.success('店舗を追加しました');
    },
  });

  const updateMut = useMutation({
    mutationFn: (form: StoreForm) => apiFetch(`/stores/${form.store_id}`, { method: 'PATCH', body: JSON.stringify(form) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['nodes'] });
      setEditing(null);
      toast.success('店舗を更新しました');
    },
  });

  const stopMut = useMutation({
    mutationFn: (storeId: string) => apiFetch(`/stores/${storeId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['nodes'] });
      const name = stopping?.store_name ?? '';
      setStopping(null);
      toast.success(`店舗「${name}」を停止しました (紐付くデータは保持されています)`);
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '停止に失敗しました');
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (editing.store_id) updateMut.mutate(editing);
    else createMut.mutate(editing);
  };

  const Row = ({ node, style }: NodeRendererProps<NodeTreeItem>) => {
    const item = node.data;
    const store = stores.find((s) => s.store_id === item.node_id);
    // Design CRITICAL対応 (2026-08-03レビュー、WCAG 1.4.1): HierarchyPage.tsxと同じ
    // 色+アイコン+テキストの3重表現で停止中を示す (色だけに頼らない)
    const isClosed = item.status !== 'active';
    return (
      <div style={style} className={`flex items-center gap-1 px-1 rounded group hover:bg-neutral-panel ${isClosed ? 'opacity-60' : ''}`}>
        {node.isInternal ? (
          <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); node.toggle(); }}
            className="flex items-center justify-center w-5 h-5 text-neutral-sub shrink-0" aria-label={node.isOpen ? '折り畳む' : '開く'}>
            {node.isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : <span className="w-5 shrink-0" />}
        <NodeTypeIconChip icon={item.icon} depth={node.level} dimmed={isClosed} />
        <span className={`text-sm truncate ${isClosed ? 'line-through text-neutral-sub' : ''}`}>{item.name}</span>
        <span className="text-[11px] text-neutral-sub shrink-0">{item.label}</span>
        <span className="text-[11px] font-mono text-neutral-sub shrink-0">{item.node_code}</span>
        {isClosed && (
          <span className="flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded bg-neutral-line text-neutral-sub shrink-0">
            <Ban size={10} aria-hidden="true" /> 停止中
          </span>
        )}
        <span className="flex-1" />
        {store && (
          <button type="button" aria-label="詳細を見る" onClick={() => openEditFromStoreId(item.node_id)}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-brand-red hover:text-brand-red-hover shrink-0 transition">
            <Pencil size={13} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-h1">{t('entity.store_nodes', '組織ノード管理')} (店舗マスタ)</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {hierarchyManagementEnabled && (
            <div className="flex rounded border border-neutral-line overflow-hidden text-sm" role="tablist" aria-label="表示モード">
              <button type="button" role="tab" aria-selected={mode === 'tree'} onClick={() => setMode('tree')}
                className={`flex items-center gap-1 px-3 py-2 ${mode === 'tree' ? 'bg-brand-red text-white' : 'hover:bg-neutral-panel'}`}>
                <GitBranch size={14} /> ツリー
              </button>
              <button type="button" role="tab" aria-selected={mode === 'flat'} onClick={() => setMode('flat')}
                className={`flex items-center gap-1 px-3 py-2 border-l border-neutral-line ${mode === 'flat' ? 'bg-brand-red text-white' : 'hover:bg-neutral-panel'}`}>
                <List size={14} /> 葉店舗のみ
              </button>
            </div>
          )}
          {effectiveMode === 'flat' && (
            <>
              {canExport && (
              <button
                type="button"
                disabled={isLoading || sortedRows.length === 0}
                aria-label="現在の絞込結果をマスタ取込CSV形式で出力"
                onClick={() => {
                  const headers = ['store_code', 'store_name', 'address', 'effective_from', 'effective_to', 'status'];
                  const rows = sortedRows.map((s) => ({
                    store_code: s.store_code,
                    store_name: s.store_name,
                    address: s.address ?? '',
                    effective_from: s.effective_from,
                    effective_to: s.effective_to ?? '',
                    status: s.status,
                  }));
                  downloadAsCsv(`stores_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
                }}
                className="flex items-center gap-1 px-3 py-2 rounded border border-neutral-line text-sm hover:bg-neutral-panel disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <Download size={14} /> CSV出力
              </button>
              )}
              <button
                onClick={() => setEditing({ store_code: '', store_name: '', effective_from: new Date().toISOString().slice(0, 10) })}
                className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap"
              >
                <Plus size={16} /> 新規登録
              </button>
            </>
          )}
        </div>
      </div>

      {effectiveMode === 'tree' ? (
        <div className="border border-neutral-line rounded-lg bg-white p-2">
          <label className="flex items-center gap-1.5 text-sm text-neutral-sub px-2 pb-2">
            <input type="checkbox" checked={showClosedTree} onChange={(e) => setShowClosedTree(e.target.checked)} />
            停止中も表示
          </label>
          {!nodesData ? (
            <div className="p-4 text-neutral-sub text-sm">読み込み中…</div>
          ) : nodesData.roots.length === 0 ? (
            <div className="p-6 text-center text-neutral-sub text-sm">ノードがありません</div>
          ) : (
            <Tree<NodeTreeItem>
              data={nodesData.roots}
              idAccessor="node_id"
              childrenAccessor="children"
              openByDefault={false}
              width="100%"
              height={520}
              indent={20}
              rowHeight={32}
            >
              {Row}
            </Tree>
          )}
          <p className="text-xs text-neutral-sub px-2 pt-2">
            店舗ノードの編集アイコンをクリックすると店舗マスタの編集フォームが開きます。組織階層自体の編集は「設定 &gt; 組織階層」で行ってください。
          </p>
        </div>
      ) : (
        <>
          <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-center gap-3 flex-wrap">
            <Filter size={14} className="text-neutral-sub" />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'closed')}
              className="px-2 py-1 border border-neutral-line rounded text-sm">
              <option value="all">全ての状態</option>
              <option value="active">稼働中のみ</option>
              <option value="closed">停止中のみ</option>
            </select>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub pointer-events-none" />
              <input type="text" value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="コード・名称・住所で検索"
                className="pl-6 pr-2 py-1 border border-neutral-line rounded text-sm w-52" />
            </div>
            <div className="flex-1" />
            <div className="text-xs text-neutral-sub tabular-nums whitespace-nowrap">
              {sortedRows.length} 件表示中（全 {stores.length} 件）
            </div>
            {hasAnyFilter && (
              <button type="button" onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-neutral-sub hover:text-brand-red">
                <X size={12} /> 絞込みをクリア
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="text-neutral-sub">読み込み中…</div>
          ) : (
            <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-neutral-panel text-neutral-sub">
                  <tr>
                    <SortableTh sortKey="store_code" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>店舗コード</SortableTh>
                    <SortableTh sortKey="store_name" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>店舗名</SortableTh>
                    <SortableTh sortKey="address" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>住所</SortableTh>
                    <SortableTh sortKey="effective_from" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>稼働期間</SortableTh>
                    <SortableTh sortKey="status" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>状態</SortableTh>
                    <th scope="col" className="w-24 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((s) => (
                    <tr key={s.store_id} className="border-t border-neutral-line hover:bg-neutral-panel">
                      <td className="px-4 py-3 tabular-nums">{s.store_code}</td>
                      <td className="px-4 py-3">{s.store_name}</td>
                      <td className="px-4 py-3 text-neutral-sub">{s.address ?? '−'}</td>
                      <td className="px-4 py-3 tabular-nums text-neutral-sub">
                        {s.effective_from}〜{s.effective_to ?? '（無期限）'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                          s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-neutral-line text-neutral-sub'
                        }`}>
                          {s.status === 'active' ? '稼働中' : '停止中'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button type="button" aria-label="編集"
                          onClick={() => openEditFromStoreId(s.store_id)}
                          className="p-1 text-brand-red hover:text-brand-red-hover"
                        >
                          <Pencil size={16} />
                        </button>
                        {s.status === 'active' && (
                          <button
                            type="button"
                            aria-label="店舗を停止"
                            onClick={() => setStopping(s)}
                            className="ml-1 p-1 text-neutral-sub hover:text-warning-text"
                            title="店舗を停止 (紐付くデータの件数を確認してから最終判断)"
                          >
                            <Ban size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedRows.length === 0 && (
                <EmptyState
                  message={hasAnyFilter ? 'フィルタ条件に一致する店舗がありません' : '店舗が登録されていません'}
                  description={hasAnyFilter ? '絞込条件を変更するか、クリアして再表示してください' : '「＋ 新規登録」から店舗を追加してください'}
                  actionLabel={hasAnyFilter ? '絞込みをクリア' : undefined}
                  onAction={hasAnyFilter ? clearFilters : undefined}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Design MAJOR対応 (2026-08-03レビュー): 自作の fixed/overlay div ではなく共有Modal
          (role="dialog"/aria-modal/Escape/フォーカストラップ完備) を使う。フォームのEnter送信と
          フッタの保存ボタンを両立させるため、ボタン側に form 属性でフォームIDを紐付ける */}
      <Modal
        open={editing !== null}
        title={editing?.store_id ? '店舗編集' : '店舗新規登録'}
        onClose={() => setEditing(null)}
        size="sm"
        footer={editing && (
          <>
            <button type="button" onClick={() => setEditing(null)}
              className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">
              キャンセル
            </button>
            <button type="submit" form="node-store-edit-form" disabled={createMut.isPending || updateMut.isPending}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {createMut.isPending || updateMut.isPending ? '保存中…' : '保存'}
            </button>
          </>
        )}
      >
        {editing && (
          <form id="node-store-edit-form" onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">店舗コード*</label>
              <input required maxLength={64} pattern="[A-Za-z0-9_-]+" value={editing.store_code}
                onChange={(e) => setEditing({ ...editing, store_code: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              <p className="text-xs text-neutral-sub mt-1">半角英数字・ハイフン・アンダースコアのみ</p>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">店舗名*</label>
              <input required maxLength={120} value={editing.store_name}
                onChange={(e) => setEditing({ ...editing, store_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">住所</label>
              <input maxLength={200} value={editing.address ?? ''}
                onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">稼働開始日*</label>
                <input required type="date" value={editing.effective_from}
                  onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">稼働終了日</label>
                <input type="date" value={editing.effective_to ?? ''}
                  onChange={(e) => setEditing({ ...editing, effective_to: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            </div>
            {editing.store_id && (
              <div>
                <label className="block text-sm text-neutral-sub mb-1">状態</label>
                <select value={editing.status ?? 'active'}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value as 'active' | 'closed' })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                  <option value="active">稼働中</option>
                  <option value="closed">停止中</option>
                </select>
              </div>
            )}
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={stopping !== null}
        title={`店舗「${stopping?.store_name ?? ''}」を停止しますか?`}
        destructive
        confirmLabel="それでも停止する"
        loading={stopMut.isPending}
        onClose={() => { if (!stopMut.isPending) setStopping(null); }}
        onConfirm={() => { if (stopping) stopMut.mutate(stopping.store_id); }}
        message={stopping && (
          <div className="space-y-3">
            <p className="text-sm">
              店舗の状態を <strong>「停止中」</strong> に変更します。
              取込済みデータは<strong>削除されません</strong>が、
              ダッシュボードから見えなくなる可能性があります。
            </p>

            {stoppingDepsLoading && (
              <div className="p-3 rounded bg-neutral-panel text-sm text-neutral-sub">
                紐付くデータ件数を確認中…
              </div>
            )}

            {stoppingDeps && stoppingDeps.total > 0 && (
              <div className="p-3 rounded bg-warning-bg border border-warning-border">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-warning-text flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-warning-text mb-1">
                      この店舗には合計 {stoppingDeps.total.toLocaleString()} 件の紐付くデータがあります
                    </div>
                    <ul className="text-xs text-neutral-text space-y-0.5">
                      {Object.entries(stoppingDeps.dependencies)
                        .filter(([, cnt]) => cnt > 0)
                        .sort(([, a], [, b]) => b - a)
                        .map(([key, cnt]) => (
                          <li key={key} className="flex justify-between gap-3">
                            <span>{DEP_LABELS[key as keyof typeof DEP_LABELS]}</span>
                            <span className="tabular-nums">{cnt.toLocaleString()} 件</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {stoppingDeps && stoppingDeps.total === 0 && (
              <div className="p-3 rounded bg-neutral-panel text-sm text-neutral-sub">
                紐付くデータはありません。安全に停止できます。
              </div>
            )}

            <p className="text-xs text-neutral-sub">
              停止後も編集画面から「稼働中」に戻すことができます。
            </p>
          </div>
        )}
      />
    </div>
  );
}
